// src/belediye/services/notificationsApi.js
// Manuel SMS + SMS Şablonları API (belediyeApi.js'e dokunmadan)

import belediyeApi from "./belediyeApi";

/**
 * Debug log (opsiyonel)
 * - .env: REACT_APP_DEBUG_SMS=1
 */
const SMS_DEBUG = String(process.env.REACT_APP_DEBUG_SMS || "").trim() === "1";
const log = (...args) => {
  if (SMS_DEBUG) console.log(...args);
};

/**
 * DRF pagination normalize:
 * - Bazı endpointler {count, next, previous, results} dönebilir
 * - Bazıları direkt array dönebilir
 */
const normalizePaginated = (data) => {
  if (!data) return { count: 0, results: [] };
  if (Array.isArray(data)) return { count: data.length, results: data };
  if (typeof data === "object" && Array.isArray(data.results)) {
    return { count: data.count ?? data.results.length, results: data.results };
  }
  // beklenmeyen format
  return { count: 0, results: [] };
};

// ------------------------------------------------------------
// Payload normalizers
// ------------------------------------------------------------

/**
 * Template payload normalize:
 * - Frontend bazen { body } gönderiyor
 * - Backend serializer body'yi kabul ediyor ama biz de garanti edelim:
 *   body -> template_text
 */
const normalizeTemplatePayload = (payload = {}) => {
  const p = { ...(payload || {}) };

  // body alias
  if (!p.template_text && p.body) {
    p.template_text = p.body;
  }
  delete p.body;

  //   unit_id desteği: frontend unit_id gönderirse backend FK alanına map et
  // (Backend alanı "unit" ise)
  if (p.unit_id != null && p.unit == null) {
    p.unit = p.unit_id;
  }
  delete p.unit_id;

  return p;
};


/**
 * Manual SMS payload normalize:
 * - Backend: text veya template_id zorunlu
 * - Frontend bazen "message" atıyor -> backend view map ediyor ama burada da garanti edelim
 */
const normalizeManualSmsPayload = (payload = {}) => {
  const p = { ...(payload || {}) };

  if (!p.text && p.message) {
    p.text = p.message;
  }
  delete p.message;

  if (p.template_id == null) delete p.template_id;

  if (typeof p.application_id === "string" && p.application_id.trim() !== "") {
    const n = Number(p.application_id);
    if (!Number.isNaN(n)) p.application_id = n;
  }

  //   unit_id'yi burada SILMEYİN; sendManualSms içinde param'a taşıyacağız.
  // (Burada bırakıyoruz ki sendManualSms alıp query param yapsın.)

  return p;
};


// ------------------------------------------------------------
// SMS Templates
// ------------------------------------------------------------

/**
 * Backend: GET /notifications/sms-templates/
 * Not: Genelde DRF pagination açık olabilir.
 */
export const fetchSmsTemplates = async (params) => {
  log("[notificationsApi] fetchSmsTemplates start", { params });
  const { data } = await belediyeApi.get("/notifications/sms-templates/", { params });
  log("[notificationsApi] fetchSmsTemplates ok", data);
  return data;
};


/**
 * UI kolaylığı: her durumda {count, results} döndürür.
 */
export const fetchSmsTemplatesNormalized = async (params) => {
  const data = await fetchSmsTemplates(params);
  return normalizePaginated(data);
};

export const createSmsTemplate = async (payload) => {
  const p = normalizeTemplatePayload(payload);
  log("[notificationsApi] createSmsTemplate start", p);
  const { data } = await belediyeApi.post("/notifications/sms-templates/", p);
  log("[notificationsApi] createSmsTemplate ok", data);
  return data;
};

export const updateSmsTemplate = async (id, payload) => {
  if (!id) throw new Error("updateSmsTemplate: id zorunlu");
  const p = normalizeTemplatePayload(payload);
  log("[notificationsApi] updateSmsTemplate start", { id, payload: p });
  const { data } = await belediyeApi.patch(`/notifications/sms-templates/${id}/`, p);
  log("[notificationsApi] updateSmsTemplate ok", data);
  return data;
};

export const deleteSmsTemplate = async (id) => {
  if (!id) throw new Error("deleteSmsTemplate: id zorunlu");
  log("[notificationsApi] deleteSmsTemplate start", { id });
  await belediyeApi.delete(`/notifications/sms-templates/${id}/`);
  log("[notificationsApi] deleteSmsTemplate ok", { id });
  return true; // DRF genelde 204 No Content döner
};

// ------------------------------------------------------------
// Manual SMS (Send + History)
// ------------------------------------------------------------

/**
 * Backend: POST /notifications/notifications/manual-sms/
 * Payload:
 *  - { application_id, text } veya { application_id, template_id }
 *  - opsiyonel: save_as_template, template_title
 */
export const sendManualSms = async (payload) => {
  const p = normalizeManualSmsPayload(payload);

  //   unit_id varsa query param'a taşı
  const unitId = p.unit_id ?? p.unitId ?? null;
  if (unitId != null) {
    delete p.unit_id;
    delete p.unitId;
  }

  log("[notificationsApi] sendManualSms start", { body: p, unit_id: unitId });

  const { data } = await belediyeApi.post(
    "/notifications/notifications/manual-sms/",
    p,
    unitId != null ? { params: { unit_id: unitId } } : undefined
  );

  log("[notificationsApi] sendManualSms ok", data);
  return data;
};


/**
 * Backend: GET /notifications/notifications/manual-sms/?application_id=123
 * Bu endpoint direkt SMS geçmişini dönüyor (NotificationSerializer list).
 */
export const fetchApplicationSms = async (applicationId, opts = {}) => {
  if (!applicationId) throw new Error("fetchApplicationSms: applicationId zorunlu");

  const appId = typeof applicationId === "string" ? Number(applicationId) : applicationId;

  const unitId = opts?.unit_id ?? opts?.unitId ?? null;

  log("[notificationsApi] fetchApplicationSms start", {
    application_id: appId,
    unit_id: unitId,
  });

  const { data } = await belediyeApi.get("/notifications/notifications/manual-sms/", {
    params: {
      application_id: appId,
      ...(unitId != null ? { unit_id: unitId } : {}),
    },
  });

  log("[notificationsApi] fetchApplicationSms ok", data);
  return data;
};

